<template>
  <div class="crestview-print-page">
    <div class="print-toolbar no-print">
      <div>
        <strong>{{ $t("Crest View Book List") }}</strong>
        <small>{{ $t("Print preview") }}</small>
      </div>
      <div>
        <b-button variant="primary" class="mr-1" @click="printPage">
          <feather-icon icon="PrinterIcon" class="mr-50" />
          {{ $t("Print") }}
        </b-button>
        <b-button variant="outline-secondary" @click="closePage">
          <feather-icon icon="XIcon" class="mr-50" />
          {{ $t("Close") }}
        </b-button>
      </div>
    </div>

    <div v-if="loading" class="print-state no-print">
      <b-spinner variant="primary" />
      <p>{{ $t("Loading book list...") }}</p>
    </div>

    <div v-else-if="error" class="print-state no-print">
      <feather-icon icon="AlertCircleIcon" size="42" class="text-danger" />
      <p class="text-danger">{{ error }}</p>
      <b-button variant="primary" @click="loadPage">{{ $t("Retry") }}</b-button>
    </div>

    <main v-else class="print-sheet">
      <header class="report-header">
        <div class="logo-frame">
          <img v-if="campus.logo" :src="campus.logo" alt="School logo" />
          <feather-icon v-else icon="BookOpenIcon" size="34" />
        </div>
        <div class="school-identity">
          <h1>{{ campus.name }}</h1>
          <p v-if="campus.branch">{{ campus.branch }}</p>
          <p v-if="campus.address">{{ campus.address }}</p>
        </div>
      </header>

      <div class="session-line">{{ sessionName }}</div>

      <section class="report-title">
        <h2>
          {{ $t("Book List") }}
          <span v-if="header.meta_class || className">
            — {{ header.meta_class || className }}
          </span>
        </h2>
        <p v-if="sectionName">{{ sectionName }}</p>
      </section>

      <section class="content-section">
        <table class="book-table">
          <thead>
            <tr>
              <th class="subject-col">{{ $t("Subject") }}</th>
              <th class="book-col">{{ $t("Book") }}</th>
              <th class="publisher-col">{{ $t("Publisher") }}</th>
              <th class="copies-col">{{ $t("Copies Required") }}</th>
              <th class="qty-col">{{ $t("Qty") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in bookRows" :key="row.key">
              <td
                v-if="row.showSubject"
                :rowspan="row.subjectRowspan"
                class="subject-cell"
              >
                {{ row.subjectName }}
              </td>
              <td class="book-cell">{{ row.particular || "—" }}</td>
              <td>{{ row.categoryName || "—" }}</td>
              <td>{{ row.copiesRequired || "—" }}</td>
              <td class="qty-cell">{{ row.qty }}</td>
            </tr>
            <tr v-if="!bookRows.length">
              <td colspan="5" class="empty-cell">{{ $t("No books found") }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="stationery-section">
        <h3>{{ $t("Stationery") }}</h3>
        <table class="stationery-table">
          <thead>
            <tr>
              <th>{{ $t("Item") }}</th>
              <th class="stationery-qty">{{ $t("Qty") }}</th>
              <th>{{ $t("Item") }}</th>
              <th class="stationery-qty">{{ $t("Qty") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in stationeryRows" :key="'stationery-' + index">
              <td>{{ row.left.name }}</td>
              <td class="qty-cell">{{ row.left.qty }}</td>
              <td>{{ row.right ? row.right.name : "" }}</td>
              <td class="qty-cell">{{ row.right ? row.right.qty : "" }}</td>
            </tr>
            <tr v-if="!stationeryRows.length">
              <td colspan="4" class="empty-cell">{{ $t("No stationery found") }}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import { BButton, BSpinner } from "bootstrap-vue";

export default {
  name: "BookListCrestViewPrint",
  components: {
    BButton,
    BSpinner,
  },
  data() {
    return {
      loading: true,
      error: "",
      header: {},
      subjects: [],
      stationary: [],
      sessionName: "Current Session",
      campus: {
        name: "Crest View Academy",
        branch: "",
        address: "",
        logo: "",
      },
    };
  },
  computed: {
    recordID() {
      return this.$route.query.id;
    },
    db() {
      return this.$route.query.db || this.$store.state.userData.db;
    },
    campusID() {
      return Number(
        this.$route.query.campusID ||
          (this.$store.state.userData && this.$store.state.userData.cId) ||
          1
      );
    },
    className() {
      return this.header.meta_class || this.header.className || "";
    },
    sectionName() {
      const value =
        this.header.meta_section || this.header.sectionName || "All Sections";
      return value === "All Sections" ? value : `${this.$t("Section")}: ${value}`;
    },
    bookRows() {
      const rows = [];
      this.subjects.forEach((subject, subjectIndex) => {
        const books = Array.isArray(subject.books) ? subject.books : [];
        books.forEach((book, bookIndex) => {
          rows.push({
            key: `${subject.subjectID || subjectIndex}-${book.id || bookIndex}`,
            showSubject: bookIndex === 0,
            subjectRowspan: books.length,
            subjectName: String(subject.subjectName || "—").trim(),
            particular: book.particular || "",
            categoryName: book.categoryName || "",
            copiesRequired: book.copiesRequired || "",
            qty: Number(book.qty || 0),
          });
        });
      });
      return rows;
    },
    stationeryRows() {
      const rows = [];
      for (let index = 0; index < this.stationary.length; index += 2) {
        rows.push({
          left: this.stationary[index],
          right: this.stationary[index + 1] || null,
        });
      }
      return rows;
    },
  },
  created() {
    this.loadPage();
  },
  methods: {
    apiBase() {
      const domain = this.$store.state.domain || "https://apiv26.myskool.app/api/";
      return domain.endsWith("/") ? domain : `${domain}/`;
    },
    authConfig(params) {
      return {
        params,
        headers: {
          Authorization: `Bearer ${this.$store.state.userData.token}`,
          accept: "application/json",
        },
      };
    },
    extractList(response) {
      let value = response && response.data !== undefined ? response.data : response;
      if (value && value.status && value.data !== undefined) value = value.data;
      if (value && !Array.isArray(value) && Array.isArray(value.data)) {
        value = value.data;
      }
      return Array.isArray(value) ? value : [];
    },
    async loadPage() {
      if (!this.recordID || !this.db) {
        this.error = this.$t("Book list information is missing.");
        this.loading = false;
        return;
      }
      this.loading = true;
      this.error = "";
      try {
        await Promise.all([
          this.loadBookList(),
          this.loadCampus(),
          this.loadSession(),
        ]);
      } catch (error) {
        this.error =
          (error.response &&
            error.response.data &&
            (error.response.data.message || error.response.data.error)) ||
          this.$t("Unable to load this book list.");
      } finally {
        this.loading = false;
      }
    },
    async loadBookList() {
      const response = await axios.get(
        `${this.apiBase()}BookList/LoadSelected`,
        this.authConfig({
          db: this.db,
          campusID: this.campusID,
          id: this.recordID,
        })
      );
      const root = response.data || {};
      const payload = root.data || root;
      this.header = payload.header || {};
      const groups = payload.groups || {};
      this.subjects = Array.isArray(groups.subjects)
        ? groups.subjects
        : this.groupItems(payload.items || []);
      this.stationary = Array.isArray(groups.stationary)
        ? groups.stationary.map((item) => ({
            id: item.id || 0,
            name: item.name || item.particular || "",
            qty: Number(item.qty || 0),
          }))
        : (payload.items || [])
            .filter((item) => item.type === "stationary")
            .map((item) => ({
              id: item.id || 0,
              name: item.particular || item.name || "",
              qty: Number(item.qty || 0),
            }));
    },
    groupItems(items) {
      const subjects = {};
      items
        .filter((item) => item.type === "book")
        .forEach((item) => {
          const key = item.subjectID || 0;
          if (!subjects[key]) {
            subjects[key] = {
              subjectID: key,
              subjectName: item.subjectName || "",
              books: [],
            };
          }
          subjects[key].books.push(item);
        });
      return Object.keys(subjects).map((key) => subjects[key]);
    },
    async loadCampus() {
      try {
        const response = await axios.get(
          `${this.apiBase()}campuses`,
          this.authConfig({ db: this.db, cId: this.campusID })
        );
        const campuses = this.extractList(response);
        const selected =
          campuses.find((item) => Number(item.id) === this.campusID) ||
          campuses[0];
        if (selected) {
          this.campus = {
            name:
              selected.name ||
              selected.organizationName ||
              this.campus.name,
            branch: selected.branch || selected.branchName || "",
            address: selected.address || "",
            logo: selected.logo || "",
          };
        }
      } catch (error) {
        const current = this.$store.state.currentBranch || {};
        this.campus.name =
          current.organizationName || current.name || this.campus.name;
        this.campus.branch = current.branch || "";
        this.campus.logo = current.logo || "";
      }
    },
    resolveCurrentSession(sessions = []) {
      if (!Array.isArray(sessions) || !sessions.length) return null;

      const activeSessionId =
        this.$store.state.sessionID ||
        this.$route.query.sessionID ||
        null;

      if (activeSessionId != null) {
        const byId = sessions.find(
          (item) => Number(item.id) === Number(activeSessionId)
        );
        if (byId) return byId;
      }

      const byFlag = sessions.find(
        (item) => item.isCurrentSession === true || item.isCurrentSession === 1
      );
      if (byFlag) return byFlag;

      return sessions[sessions.length - 1];
    },
    async loadSession() {
      try {
        const response = await axios.get(
          `${this.apiBase()}Sessions`,
          this.authConfig({ db: this.db, cID: this.campusID })
        );
        const sessions = this.extractList(response);
        const current = this.resolveCurrentSession(sessions);
        if (current) {
          this.sessionName = current.session || current.name || this.sessionName;
        }
      } catch (error) {
        const stored =
          (this.$store.state.userData && this.$store.state.userData.sessions) ||
          [];
        const current = this.resolveCurrentSession(stored);
        if (current) {
          this.sessionName = current.session || current.name || this.sessionName;
        }
      }
    },
    printPage() {
      window.print();
    },
    closePage() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.crestview-print-page {
  min-height: 100vh;
  padding: 24px;
  color: #1e293b;
  background: #e7ebf1;
  font-family: Arial, Helvetica, sans-serif;
}

.print-toolbar {
  width: 210mm;
  max-width: 100%;
  margin: 0 auto 16px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.08);

  small {
    display: block;
    color: #6e6b7b;
  }
}

.print-state {
  padding: 80px 16px;
  text-align: center;

  p {
    margin: 12px 0;
  }
}

.print-sheet {
  position: relative;
  box-sizing: border-box;
  width: 210mm;
  min-height: 297mm;
  max-width: 100%;
  margin: 0 auto;
  padding: 14mm 12mm;
  background: #fff;
  box-shadow: 0 8px 30px rgba(15, 23, 42, 0.14);
}

.report-header {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 92px;
  padding-bottom: 12px;
  text-align: center;
}

.logo-frame {
  width: 72px;
  height: 72px;
  margin-right: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #243b64;

  img {
    width: 68px;
    max-height: 68px;
    object-fit: contain;
  }
}

.school-identity {
  min-width: 360px;

  h1 {
    margin: 0;
    color: #1e3153;
    font-size: 25px;
    font-weight: 800;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  p {
    margin: 3px 0 0;
    color: #475569;
    font-size: 12px;
    line-height: 1.4;
  }
}

.session-line {
  margin: 4px 0 8px;
  color: #1e3153;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.4px;
}

.report-title {
  margin: 10px 0 12px;
  text-align: center;

  h2 {
    display: inline-block;
    margin: 0;
    padding-bottom: 3px;
    color: #1e3153;
    border-bottom: 2px solid #1e3153;
    font-size: 20px;
    font-weight: 800;
    text-transform: uppercase;
  }

  p {
    margin: 5px 0 0;
    color: #475569;
    font-size: 12px;
  }
}

.content-section {
  overflow: hidden;
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 11.5px;
  line-height: 1.35;
}

th,
td {
  padding: 7px 8px;
  border: 1px solid #cfd7e2;
  vertical-align: top;
}

th {
  color: #172033;
  background: #dfe3e8;
  text-align: center;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.subject-col {
  width: 19%;
}

.book-col {
  width: 33%;
}

.publisher-col {
  width: 15%;
}

.copies-col {
  width: 25%;
}

.qty-col {
  width: 8%;
}

.subject-cell,
.qty-cell {
  text-align: center;
}

.subject-cell {
  color: #1e3153;
  background: #f4f5f7;
  font-weight: 700;
}

.qty-cell {
  font-weight: 700;
}

.book-cell {
  color: #1f2937;
  font-weight: 600;
  white-space: pre-line;
}

.stationery-section {
  margin-top: 22px;
  break-inside: avoid;

  h3 {
    margin: 0 0 8px;
    color: #1e3153;
    text-align: center;
    font-size: 17px;
    font-weight: 800;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
}

.stationery-qty {
  width: 9%;
}

.empty-cell {
  padding: 18px;
  text-align: center;
  color: #666;
}

@media print {
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .crestview-print-page {
    min-height: 0;
    padding: 0;
    background: #fff;
  }

  .no-print {
    display: none !important;
  }

  .print-sheet {
    width: auto;
    min-height: 0;
    margin: 0;
    padding: 0;
    box-shadow: none;
  }

  thead {
    display: table-header-group;
  }

  tr {
    break-inside: avoid;
  }
}

@page {
  size: A4 portrait;
  margin: 10mm;
}

@media (max-width: 768px) {
  .crestview-print-page {
    padding: 10px;
  }

  .print-toolbar {
    align-items: flex-start;
    gap: 10px;
  }

  .report-header {
    align-items: center;
  }

  .logo-frame {
    width: 60px;
    height: 60px;
    margin-right: 10px;
  }

  .school-identity {
    min-width: 0;
  }

  .print-sheet {
    padding: 18px 12px;
    overflow-x: auto;
  }
}
</style>
